import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import googleAuthenticatorLogo from 'styles/assets/png/google-authenticator-logo.png';
import microsoftAuthenticatorLogo from 'styles/assets/png/microsoft-authenticator-logo.png';

import { useQueryState } from 'hooks/useQueryState';

import { useLoginStore } from 'store/login.store';

import { useMutationLogin } from 'api/mutations/login.mutation';
import { useQueryGetUserDetail } from 'api/queries/getUserDetail.query';
import { useQueryServerTime } from 'api/queries/serverTime.query';

import { GENERIC_ERROR, encrypt } from 'components/App/AuthPublicPages/NewDesign/Login/Login.utils';
import classes from 'components/App/AuthPublicPages/NewDesign/Mfa/Mfa.module.scss';
import { Layout } from 'components/App/AuthPublicPages/components/Layout/Layout';
import { useLoginHook } from 'components/App/AuthPublicPages/hooks/loginHook';
import Button from 'components/DesignSystem/Button/Button';
import TextInput from 'components/DesignSystem/TextInput/TextInput';
import { CustomizedFormErrorMessage } from 'components/common';

export const Mfa = () => {
    const history = useHistory();
    const [type] = useQueryState('type');
    const [emailAfterMfa, setEmailAfterMfa] = useState<string | undefined>(undefined);
    const [error, setError] = useState<string>('');
    const { data: serverTime } = useQueryServerTime();
    const { username, password } = useLoginStore();
    const { mutate: loginMutation, isLoading: isLoadingLogin } = useMutationLogin();
    const { data: user, isLoading: isLoadingUserDetail } = useQueryGetUserDetail({
        email: emailAfterMfa,
    });

    useLoginHook({ user, isSwitchUser: false });

    const form = useForm<{ mfaCode: string }>({
        defaultValues: {
            mfaCode: '',
        },
    });

    const handleLogin = ({ mfaCode }: { mfaCode: string }) => {
        if (!username || !password) return null;
        const time = serverTime?.time as number;
        const payload =
            time !== 0
                ? { encrypt: encrypt(JSON.stringify({ username, password, mfaCode }), time), time }
                : { username, password, mfaCode };
        loginMutation(payload, {
            onSuccess: (response) => {
                if (response.email) {
                    setEmailAfterMfa(response.email);
                    setError('');
                } else {
                    setError(GENERIC_ERROR);
                }
            },
            onError: () => {
                setError(GENERIC_ERROR);
            },
        });
    };

    useEffect(() => {
        if (!username || !password) {
            history.push('/login');
        }
    }, [username, password]);

    return (
        <Layout>
            <div className={classes.container}>
                <div className={classes.title}>Enter two-factor authentication code</div>
                <p className={classes.subTitle}>
                    {type === 'email'
                        ? `An email with code has been sent to ${username}`
                        : `Enter the code from ${type} authenticator app`}
                    {type && ['google', 'microsoft'].includes(type) && (
                        <img
                            className={classes.authLogo}
                            src={type === 'google' ? googleAuthenticatorLogo : microsoftAuthenticatorLogo}
                            alt={type}
                        />
                    )}
                </p>
                <form onSubmit={form.handleSubmit(handleLogin)} autoComplete={'off'}>
                    <div className={classes.formRows}>
                        <div className={classes.row}>
                            <label className={classes.label} htmlFor={'mfaCode'}>
                                Enter your 6-digit code
                            </label>
                            <Controller
                                render={({ field }) => (
                                    <TextInput
                                        {...field}
                                        id={'mfaCode'}
                                        className={form.formState.errors.mfaCode ? classes.invalid : ''}
                                        maxLength={6}
                                    />
                                )}
                                name={'mfaCode'}
                                control={form.control}
                                rules={{ required: 'Code is Required' }}
                            />
                            {form.formState.errors.mfaCode && (
                                <CustomizedFormErrorMessage text={form.formState.errors.mfaCode.message} />
                            )}
                            {error && <CustomizedFormErrorMessage text={error} />}
                        </div>

                        <p className={classes.row}>
                            We're asking for this code based on your two-factor authentication preferences.
                        </p>
                        <div className={classes.row}>
                            <Button
                                variant={'primary'}
                                disabled={isLoadingLogin || isLoadingUserDetail}
                                isLoading={isLoadingLogin || isLoadingUserDetail}
                                id={'login-button'}
                                type={'submit'}
                            >
                                Sign In
                            </Button>
                        </div>
                        <div className={classes.backToLoginLink}>
                            Having trouble? <Link to={'/login'}>Retry sign in</Link>, or{' '}
                            <a href={'mailto:cs@foresight.works'}>contact us</a> for help
                        </div>
                    </div>
                </form>
            </div>
        </Layout>
    );
};
